import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { X, UserPlus, CheckCircle } from 'lucide-react'
import { inviteFieldSurveyor } from '../../lib/services/facilityContacts'
import { getFacilities } from '../../lib/services/facilities'

interface InviteFieldSurveyorModalProps {
  onClose: () => void
}

export function InviteFieldSurveyorModal({ onClose }: InviteFieldSurveyorModalProps) {
  const qc = useQueryClient()
  const [selectedFacilityIds, setSelectedFacilityIds] = useState<Set<string>>(new Set())
  const [error, setError] = useState<string | null>(null)

  const { data: facilities = [], isLoading: facilitiesLoading } = useQuery({
    queryKey: ['facilities-for-invite'],
    queryFn: () => getFacilities(),
  })

  const inviteMutation = useMutation({
    mutationFn: (formData: FormData) =>
      inviteFieldSurveyor({
        email: (formData.get('email') as string).trim(),
        facility_ids: [...selectedFacilityIds],
        first_name: ((formData.get('first_name') as string) || '').trim() || undefined,
        last_name: ((formData.get('last_name') as string) || '').trim() || undefined,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-users'] })
      setError(null)
    },
    onError: (err: Error) => setError(err.message),
  })

  function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault()
    if (selectedFacilityIds.size === 0) {
      setError('Select at least one facility to assign')
      return
    }
    inviteMutation.mutate(new FormData(e.currentTarget))
  }

  function toggleFacility(id: string) {
    setSelectedFacilityIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-md rounded-2xl bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-slate-100 px-6 py-4">
          <h2 className="flex items-center gap-2 text-base font-bold text-slate-800">
            <UserPlus size={18} className="text-blue-600" />
            Invite Field Surveyor
          </h2>
          <button onClick={onClose} className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100">
            <X size={18} />
          </button>
        </div>

        {inviteMutation.isSuccess ? (
          <div className="px-6 py-8 text-center">
            <CheckCircle size={40} className="mx-auto mb-3 text-green-500" />
            <p className="font-bold text-slate-800">Invitation sent</p>
            <p className="mt-1 text-sm text-slate-500">
              The surveyor will receive a magic-link to set up their account.
              They will be assigned to {inviteMutation.data.facility_assignment_count} facility
              {inviteMutation.data.facility_assignment_count !== 1 ? 'ies' : 'y'}.
            </p>
            <button
              onClick={onClose}
              className="mt-6 rounded-lg bg-blue-600 px-5 py-2 text-sm font-bold text-white hover:bg-blue-700"
            >
              Done
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="px-6 py-5">
            {error && (
              <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                {error}
              </div>
            )}

            <div className="space-y-4">
              <div>
                <label className="mb-1 block text-xs font-bold text-slate-600">Email *</label>
                <input
                  name="email"
                  type="email"
                  required
                  className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                  placeholder="surveyor@example.com"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="mb-1 block text-xs font-bold text-slate-600">First name</label>
                  <input
                    name="first_name"
                    className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                    placeholder="Optional"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-xs font-bold text-slate-600">Last name</label>
                  <input
                    name="last_name"
                    className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                    placeholder="Optional"
                  />
                </div>
              </div>

              <div>
                <label className="mb-1 block text-xs font-bold text-slate-600">
                  Assign to facilities *
                </label>
                {facilitiesLoading ? (
                  <p className="text-sm text-slate-500">Loading facilities…</p>
                ) : facilities.length === 0 ? (
                  <p className="text-sm text-slate-500">No facilities found in this tenant.</p>
                ) : (
                  <ul className="max-h-48 space-y-1.5 overflow-y-auto rounded-lg border border-slate-200 p-2">
                    {facilities.map((f) => (
                      <li key={f.facility_id}>
                        <label className="flex cursor-pointer items-center gap-3 rounded-lg px-2 py-1.5 hover:bg-slate-50">
                          <input
                            type="checkbox"
                            checked={selectedFacilityIds.has(f.facility_id)}
                            onChange={() => toggleFacility(f.facility_id)}
                            className="rounded"
                          />
                          <span className="text-sm font-semibold text-slate-700">{f.facility_name}</span>
                        </label>
                      </li>
                    ))}
                  </ul>
                )}
                {selectedFacilityIds.size > 0 && (
                  <p className="mt-1 text-xs text-slate-500">
                    {selectedFacilityIds.size} facility{selectedFacilityIds.size !== 1 ? 'ies' : 'y'} selected
                  </p>
                )}
              </div>
            </div>

            <div className="mt-6 flex justify-end gap-2">
              <button
                type="button"
                onClick={onClose}
                className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={inviteMutation.isPending}
                className="flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-bold text-white hover:bg-blue-700 disabled:opacity-50"
              >
                <UserPlus size={16} />
                {inviteMutation.isPending ? 'Sending…' : 'Send Invite'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  )
}
